import {
  createSignal,
  createResource,
  createEffect,
  For,
  Show,
  createMemo,
  type JSX,
} from "solid-js";

import type { LocationId } from "~/api";
import {
  pearlAnnotationsLocationApiV1ClinicalPhotoPath,
  reanalyzePearlLocationApiV1ClinicalPhotoPath,
} from "~/routes";
import { coreFetch } from "~/request";

import { PearlMeasurementMark } from "./pearl_measurement_mark";
import type { PearlAnnotation, PearlLineSegment, PearlResult, PearlToothPart } from "./pearl_types";

type FetchResult = PearlResult | { loading: true; status: string } | null;

async function reanalyzeAnnotations(locationId: LocationId, photoId: string): Promise<void> {
  await coreFetch(reanalyzePearlLocationApiV1ClinicalPhotoPath(locationId, photoId), {
    method: "POST",
  });
}

export async function fetchPearlAnnotations(
  locationId: LocationId,
  photoId: string,
): Promise<FetchResult> {
  try {
    const response = await fetch(
      pearlAnnotationsLocationApiV1ClinicalPhotoPath(locationId, photoId),
    );
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error("Failed to fetch Pearl annotations");
    }
    const rawData = (await response.json()) as Record<string, unknown>;

    if (rawData["loading"] === true) {
      return { loading: true, status: rawData["status"] as string };
    }

    const data = (rawData["result"] ?? rawData) as PearlResult;

    if (!data.is_completed) {
      return { loading: true, status: "processing" };
    }

    if (!Array.isArray(data.annotations)) {
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function isLoadingResult(
  result: FetchResult | undefined,
): result is { loading: true; status: string } {
  return result !== null && result !== undefined && "loading" in result;
}

export function isPearlResult(result: FetchResult | undefined): result is PearlResult {
  return result !== null && result !== undefined && !isLoadingResult(result);
}

function hasLineSegment(
  lineSegment: PearlLineSegment | Record<string, never>,
): lineSegment is PearlLineSegment {
  return "x1" in lineSegment;
}

function formatAnnotationTooltip(
  annotation: PearlAnnotation,
): { title: string; subtitle?: string } | null {
  const category = getAnnotationCategory(annotation);
  const baseCategory = normalizeCategory(annotation.category);
  const config = CATEGORY_CONFIG[category];
  const baseLabel = config?.label || annotation.category;

  if (baseCategory === "caries") {
    const relationships = annotation.relationships || [];

    // Filter for anatomy parts only
    const anatomyParts = ["cementum", "dentin", "enamel", "pulp"];
    const anatomyRels = relationships.filter((r) =>
      anatomyParts.some((part) => r.prop_value.includes(part)),
    );

    // Calculate total for rescaling to 100%
    const total = anatomyRels.reduce((sum, r) => sum + r.metric_value, 0);

    if (total > 0) {
      const dentinRel = anatomyRels.find((r) => r.prop_value.includes("dentin"));
      const hasDentin = dentinRel && dentinRel.metric_value > 0;
      const severity = hasDentin ? "Progressed" : "Incipient";

      // Build percentage breakdown
      const breakdown = anatomyRels
        .filter((r) => r.metric_value > 0)
        .map((r) => {
          const partName = anatomyParts.find((p) => r.prop_value.includes(p)) || "";
          const displayName = partName.charAt(0).toUpperCase() + partName.slice(1);
          const percent = Math.round((r.metric_value / total) * 100);
          return `${displayName} ${percent}%`;
        })
        .join(", ");

      return {
        title: `Caries - ${severity}`,
        subtitle: breakdown || undefined,
      };
    }

    return { title: "Caries" };
  }

  if (category === "filling") {
    return { title: "Filling - non-metal" };
  }

  return { title: baseLabel };
}

function normalizeCategory(category: string): string {
  // Remove condition prefixes
  let normalized = category
    .replace(/^conditions\.pathologies\./, "")
    .replace(/^conditions\.anatomy\./, "")
    .replace(/^conditions\./, "");

  // Convert dots and spaces to underscores, lowercase
  normalized = normalized.toLowerCase().replace(/[.\s]+/g, "_");

  // Handle Pearl condition/category aliases
  const aliases: Record<string, string> = {
    restoration_e: "restoration",
    bo: "bone",
    ian: "inferior_alveolar_nerve",
    si: "sinus",
    nc: "nasal_cavity",
    ca_eo: "caries", // conditions.pathologies.ca.eo
    margin_discrepancy: "notable_margin", // Pearl uses both names
  };

  return aliases[normalized] || normalized;
}

function getAnnotationCategory(annotation: PearlAnnotation): string {
  if (hasLineSegment(annotation.line_segment)) {
    return "measurements";
  }

  const baseCategory = normalizeCategory(annotation.category);

  if (baseCategory === "caries") {
    const relationships = annotation.relationships || [];
    const dentinRel = relationships.find((r) => r.prop_value.includes("dentin"));
    const hasDentin = dentinRel && dentinRel.metric_value > 0;
    return hasDentin ? "caries_progressed" : "caries_incipient";
  }

  return baseCategory;
}
function getCategories(
  annotations: PearlAnnotation[] | undefined,
  toothParts: PearlToothPart[] | undefined,
): string[] {
  const categories = new Set<string>();
  if (annotations) {
    annotations.forEach((a) => categories.add(getAnnotationCategory(a)));
  }
  if (toothParts) {
    toothParts.forEach((tp) => categories.add(normalizeCategory(tp.condition)));
  }
  return Array.from(categories).sort();
}

type CategoryGroup = "pathology" | "non_pathology" | "tooth_parts";

type CategoryInfo = {
  label: string;
  color: string;
  group: CategoryGroup;
};

const CATEGORY_CONFIG: Record<string, CategoryInfo> = {
  caries: { label: "Caries", color: "#FB19DA", group: "pathology" },
  caries_progressed: { label: "Caries - Progressed", color: "#FB19DA", group: "pathology" },
  caries_incipient: { label: "Caries - Incipient", color: "#FFB52D", group: "pathology" },
  calculus: { label: "Calculus", color: "#00FFE0", group: "pathology" },
  periapical_radiolucency: {
    label: "Periapical radiolucency",
    color: "#35CFFA",
    group: "pathology",
  },
  notable_margin: { label: "Notable margin", color: "#7B42F6", group: "pathology" },

  measurements: { label: "Measurements", color: "#C4C4C4", group: "non_pathology" },
  crown: { label: "Crown", color: "#C4C4C4", group: "non_pathology" },
  filling: { label: "Filling", color: "#C4C4C4", group: "non_pathology" },
  bridge: { label: "Bridge", color: "#C4C4C4", group: "non_pathology" },
  implant: { label: "Implant", color: "#C4C4C4", group: "non_pathology" },
  root_canal: { label: "Root canal", color: "#C4C4C4", group: "non_pathology" },

  bone: { label: "Bone", color: "#FFE085", group: "tooth_parts" },
  enamel: { label: "Enamel", color: "#FFFFFF", group: "tooth_parts" },
  dentin: { label: "Dentin", color: "#92F7C7", group: "tooth_parts" },
  pulp: { label: "Pulp", color: "#AC66FF", group: "tooth_parts" },
  cementum: { label: "Cementum", color: "#85DAFF", group: "tooth_parts" },
  restoration: { label: "Restoration", color: "#FFBEA1", group: "tooth_parts" },

  inferior_alveolar_nerve: {
    label: "Inferior alveolar nerve",
    color: "#FFD700",
    group: "tooth_parts",
  },
  sinus: { label: "Sinus", color: "#87CEEB", group: "tooth_parts" },
  nasal_cavity: { label: "Nasal cavity", color: "#DDA0DD", group: "tooth_parts" },
};

const MEASUREMENT_SWATCH = "linear-gradient(90deg, #6dd26e, #fade52, #ff5f4a)";

const GROUP_LABELS: Record<CategoryGroup, string> = {
  pathology: "Pathology",
  non_pathology: "Non-pathology",
  tooth_parts: "Tooth parts",
};

// What the overlay shows before anyone touches its checkboxes: every pathology and measurement, and
// the tooth parts only where the office turned them on by default.
function defaultVisibleCategories(categories: string[], toothPartsDefaultOn: boolean): Set<string> {
  const visible = new Set<string>();
  for (const cat of categories) {
    const config = CATEGORY_CONFIG[cat];
    const group = config?.group || "non_pathology";
    if (
      group === "pathology" ||
      (group === "tooth_parts" && toothPartsDefaultOn) ||
      cat === "measurements" ||
      cat === "anatomy"
    ) {
      visible.add(cat);
    }
  }
  return visible;
}

function shownAnnotations(data: PearlResult, visible: Set<string>): PearlAnnotation[] {
  return data.annotations.filter(
    (a) => visible.has(getAnnotationCategory(a)) && !a.is_dismissed && !a.is_deleted,
  );
}

function shownToothParts(data: PearlResult, visible: Set<string>): PearlToothPart[] {
  return (data.toothParts ?? []).filter((tp) => visible.has(normalizeCategory(tp.condition)));
}

/**
 * The drawing the overlay opens on, for a surface with no checkboxes of its own (the chart's hover
 * preview), plus one legend entry per finding category it draws. Tooth parts are shading, not
 * findings, so they get no entry.
 */
export function defaultPearlShapes(data: PearlResult, toothPartsDefaultOn: boolean) {
  const visible = defaultVisibleCategories(
    getCategories(data.annotations, data.toothParts),
    toothPartsDefaultOn,
  );
  const annotations = shownAnnotations(data, visible);

  const counts = new Map<string, number>();
  for (const annotation of annotations) {
    const category = getAnnotationCategory(annotation);
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }
  const findings = Array.from(counts, ([category, count]) => ({
    category,
    label: CATEGORY_CONFIG[category]?.label ?? category,
    swatch:
      category === "measurements"
        ? MEASUREMENT_SWATCH
        : (CATEGORY_CONFIG[category]?.color ?? "#888"),
    count,
  }));

  return { annotations, toothParts: shownToothParts(data, visible), findings };
}

const PATIENT_PEARL_CATEGORIES = [
  "caries_progressed",
  "caries_incipient",
  "calculus",
  "periapical_radiolucency",
  "notable_margin",
] as const;

export type PatientPearlCategory = (typeof PATIENT_PEARL_CATEGORIES)[number];

function isPatientPearlCategory(category: string): category is PatientPearlCategory {
  return PATIENT_PEARL_CATEGORIES.some((patientCategory) => patientCategory === category);
}

/**
 * What a patient-facing picture draws: the findings only, never anatomy, tooth parts, restorations or measurements.
 * The server filters the same way; this second pass also counts the findings for the legend.
 */
export function patientPearlShapes(data: Pick<PearlResult, "annotations">): {
  annotations: PearlAnnotation[];
  findings: { category: PatientPearlCategory; swatch: string; count: number }[];
} {
  const annotations: PearlAnnotation[] = [];
  const counts = new Map<PatientPearlCategory, number>();
  for (const annotation of data.annotations) {
    if (annotation.is_dismissed || annotation.is_deleted) continue;
    const category = getAnnotationCategory(annotation);
    if (!isPatientPearlCategory(category)) continue;
    annotations.push(annotation);
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }
  const findings = PATIENT_PEARL_CATEGORIES.flatMap((category) => {
    const count = counts.get(category);
    if (count === undefined) return [];
    return [{ category, swatch: CATEGORY_CONFIG[category]?.color ?? "#888", count }];
  });
  return { annotations, findings };
}

// A small slide picture of a large x-ray would shrink Pearl's own strokes to hairlines.
const NON_SCALING_STROKE_WIDTH = 2.5;

type ImageBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type PearlAnnotationsOverlayProps = {
  photoId: string;
  locationId: LocationId;
  imageBounds: ImageBounds | null;
  zoomPercent: number;
  rotation?: number;
  flip?: boolean;
  flop?: boolean;
  hasPearlData?: boolean;
  visible?: boolean;
  imageReady?: boolean;
  hideAnnotations?: boolean;
  allAnnotationsVisible?: boolean;
  onAllAnnotationsVisibleChange?: (visible: boolean) => void;
  toothPartsDefaultOn?: boolean;
  onPanelExpandedChange?: (expanded: boolean) => void;
  /** Content to render below the Pearl panel (e.g., adjustments) */
  belowPanelContent?: () => import("solid-js").JSX.Element;
};

/**
 * Pearl's findings drawn over an image: an SVG in Pearl's own image space, stretched over the box
 * the (rotated) image fills. The viewer's overlay and the chart's hover preview both draw with it.
 */
export function PearlShapes(props: {
  data: Pick<PearlResult, "width" | "height">;
  annotations: PearlAnnotation[];
  toothParts: PearlToothPart[];
  // Pearl image pixels per screen pixel, so a measurement's line and label keep their screen size.
  imagePxPerScreenPx: number;
  class?: string;
  style?: JSX.CSSProperties;
  hoveredAnnotationId?: string | null;
  /** Makes each finding a hover target. Without it the drawing never takes a pointer event. */
  onAnnotationHover?: (id: string | null, point: { x: number; y: number } | null) => void;
  /** Polygons and boxes keep a fixed screen stroke, whatever the picture's size. */
  nonScalingStroke?: boolean;
}) {
  const dimensions = () => ({ width: props.data.width, height: props.data.height });

  return (
    <svg
      class={props.class}
      style={props.style}
      viewBox={`0 0 ${dimensions().width} ${dimensions().height}`}
      preserveAspectRatio="none"
    >
      <For each={props.toothParts}>
        {(toothPart) => {
          const dims = dimensions();
          return (
            <Show when={toothPart.polygon.length > 0}>
              <polygon
                points={toothPart.polygon
                  .map((p) => `${p.x * dims.width},${p.y * dims.height}`)
                  .join(" ")}
                fill={toothPart.color?.fill_color || "transparent"}
              />
            </Show>
          );
        }}
      </For>

      <For each={props.annotations}>
        {(annotation) => {
          const isHovered = () => props.hoveredAnnotationId === annotation.enml_annotation_id;
          const annotationConfig = CATEGORY_CONFIG[getAnnotationCategory(annotation)];
          const measurementLine = hasLineSegment(annotation.line_segment)
            ? annotation.line_segment
            : null;
          // Measurements carry Pearl's own severity color; a category color flattens them.
          const paletteColor = measurementLine ? undefined : annotationConfig?.color;

          const strokeColor = () => {
            return paletteColor || annotation.stroke_color;
          };

          const fillColor = () => {
            if (isHovered()) {
              return "transparent";
            }

            if (paletteColor && annotation.fill_color) {
              const pearlFill = annotation.fill_color;
              const configColor = paletteColor;

              if (pearlFill.startsWith("#") && pearlFill.length === 9) {
                const alpha = pearlFill.slice(7, 9);
                return configColor + alpha;
              }

              if (pearlFill.startsWith("rgba")) {
                const match = pearlFill.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
                if (match) {
                  const alpha = match[4];
                  if (configColor.startsWith("#")) {
                    const r = parseInt(configColor.slice(1, 3), 16);
                    const g = parseInt(configColor.slice(3, 5), 16);
                    const b = parseInt(configColor.slice(5, 7), 16);
                    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                  }
                }
              }
            }

            return annotation.fill_color || "transparent";
          };

          return (
            <g
              class={props.onAnnotationHover ? "pointer-events-auto cursor-pointer" : undefined}
              onMouseEnter={(e) => {
                props.onAnnotationHover?.(annotation.enml_annotation_id, {
                  x: e.clientX,
                  y: e.clientY,
                });
              }}
              onMouseMove={(e) => {
                props.onAnnotationHover?.(annotation.enml_annotation_id, {
                  x: e.clientX,
                  y: e.clientY,
                });
              }}
              onMouseLeave={() => {
                props.onAnnotationHover?.(null, null);
              }}
            >
              <Show when={annotation.polygon.length > 0}>
                <polygon
                  points={annotation.polygon
                    .map((p) => `${p.x * dimensions().width},${p.y * dimensions().height}`)
                    .join(" ")}
                  stroke={strokeColor()}
                  fill={fillColor()}
                  stroke-width={
                    props.nonScalingStroke ? NON_SCALING_STROKE_WIDTH : annotation.stroke_width
                  }
                  vector-effect={props.nonScalingStroke ? "non-scaling-stroke" : undefined}
                />
              </Show>

              <Show
                when={
                  annotation.polygon.length === 0 &&
                  annotation.contour_box.width > 0 &&
                  !hasLineSegment(annotation.line_segment) &&
                  getAnnotationCategory(annotation) !== "measurements" &&
                  normalizeCategory(annotation.category) !== "anatomy"
                }
              >
                {(() => {
                  const config = CATEGORY_CONFIG[getAnnotationCategory(annotation)];
                  const isNonPathology = config?.group === "non_pathology";
                  const cornerRadius = 6;
                  const boxStroke = isNonPathology ? "rgba(255, 255, 255, 0.9)" : strokeColor();
                  const boxStrokeWidth = isNonPathology
                    ? 2.5
                    : Math.max(annotation.stroke_width, 2);

                  return (
                    <>
                      <rect
                        x={annotation.contour_box.x}
                        y={annotation.contour_box.y}
                        width={annotation.contour_box.width}
                        height={annotation.contour_box.height}
                        rx={cornerRadius}
                        ry={cornerRadius}
                        stroke={boxStroke}
                        fill={fillColor()}
                        stroke-width={
                          props.nonScalingStroke ? NON_SCALING_STROKE_WIDTH : boxStrokeWidth
                        }
                        vector-effect={props.nonScalingStroke ? "non-scaling-stroke" : undefined}
                      />
                      <Show when={isNonPathology && !isHovered()}>
                        <rect
                          x={annotation.contour_box.x}
                          y={annotation.contour_box.y}
                          width={annotation.contour_box.width}
                          height={annotation.contour_box.height}
                          rx={cornerRadius}
                          ry={cornerRadius}
                          stroke="none"
                          fill="rgba(0, 0, 0, 0.15)"
                        />
                      </Show>
                    </>
                  );
                })()}
              </Show>

              <Show
                when={measurementLine}
                fallback={
                  <>
                    <For each={annotation.endpoints ?? []}>
                      {(endpoint) => (
                        <circle
                          cx={endpoint.x}
                          cy={endpoint.y}
                          r={Math.max(endpoint.radius * 0.3, 1.5)}
                          fill={strokeColor()}
                        />
                      )}
                    </For>
                    <Show when={annotation.text}>
                      {(text) => (
                        <text
                          x={text().x}
                          y={text().y}
                          fill={annotation.text_color || "#fff"}
                          font-size={`${text().font_size || 12}px`}
                          font-family="sans-serif"
                        >
                          {text().text}
                        </text>
                      )}
                    </Show>
                  </>
                }
              >
                {(lineSegment) => (
                  <PearlMeasurementMark
                    lineSegment={lineSegment()}
                    endpoints={annotation.endpoints}
                    text={annotation.text}
                    color={strokeColor()}
                    imagePxPerScreenPx={props.imagePxPerScreenPx}
                  />
                )}
              </Show>
            </g>
          );
        }}
      </For>
    </svg>
  );
}

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 30;

export function PearlAnnotationsOverlay(props: PearlAnnotationsOverlayProps) {
  const resourceKey = () => {
    if (!props.hasPearlData) return null;
    return {
      photoId: props.photoId,
      locationId: props.locationId,
      rotation: props.rotation ?? 0,
      flip: props.flip ?? false,
      flop: props.flop ?? false,
    };
  };

  const [fetchResult, { refetch }] = createResource(resourceKey, (key) =>
    fetchPearlAnnotations(key.locationId, key.photoId),
  );

  const toothPartsDefaultOn = () => props.toothPartsDefaultOn ?? false;

  const [visibleCategories, setVisibleCategories] = createSignal<Set<string>>(new Set());
  const [defaultsAppliedFor, setDefaultsAppliedFor] = createSignal<string>("");
  const [hoveredAnnotationId, setHoveredAnnotationId] = createSignal<string | null>(null);
  const [isPanelExpanded, setIsPanelExpandedInternal] = createSignal(true);
  const setIsPanelExpanded = (expanded: boolean) => {
    setIsPanelExpandedInternal(expanded);
    props.onPanelExpandedChange?.(expanded);
  };
  const [localAllAnnotationsVisible, setLocalAllAnnotationsVisible] = createSignal(true);
  const allAnnotationsVisible = () => props.allAnnotationsVisible ?? localAllAnnotationsVisible();
  const setAllAnnotationsVisible = (visible: boolean) => {
    if (props.onAllAnnotationsVisibleChange) {
      props.onAllAnnotationsVisibleChange(visible);
    } else {
      setLocalAllAnnotationsVisible(visible);
    }
  };
  const [tooltipPosition, setTooltipPosition] = createSignal<{ x: number; y: number } | null>(null);
  const [loadedPhotoId, setLoadedPhotoId] = createSignal<string>("");
  let pollAttemptCount = 0;
  const [pollExhausted, setPollExhausted] = createSignal(false);
  const [reanalyzing, setReanalyzing] = createSignal(false);

  createEffect(() => {
    resourceKey();
    pollAttemptCount = 0;
    setPollExhausted(false);
  });

  const handleReanalyze = async () => {
    setReanalyzing(true);
    pollAttemptCount = 0;
    setPollExhausted(false);
    try {
      await reanalyzeAnnotations(props.locationId, props.photoId);
    } catch {
      // ignore — the refetch will pick up current state
    }
    setReanalyzing(false);
    refetch();
  };

  const pearlData = createMemo(() => {
    const result = fetchResult();
    if (result && isPearlResult(result)) {
      return result;
    }
    return null;
  });

  const isPolling = createMemo(() => {
    const result = fetchResult();
    return result !== null && isLoadingResult(result) && !pollExhausted();
  });

  createEffect(() => {
    const result = fetchResult();
    if (result && isLoadingResult(result) && !pollExhausted()) {
      const timer = setTimeout(() => {
        pollAttemptCount += 1;
        if (pollAttemptCount >= MAX_POLL_ATTEMPTS) {
          setPollExhausted(true);
        } else {
          refetch();
        }
      }, POLL_INTERVAL_MS);
      return () => clearTimeout(timer);
    }
  });

  createEffect(() => {
    const imageReady = props.imageReady ?? true;
    const data = pearlData();
    const currentPhotoId = props.photoId;

    if (imageReady && data && !fetchResult.loading) {
      if (loadedPhotoId() !== currentPhotoId) {
        setVisibleCategories(new Set<string>());
        setDefaultsAppliedFor("");
        setHoveredAnnotationId(null);
        setTooltipPosition(null);
        // Note: allAnnotationsVisible is intentionally NOT reset here
        // so that the user's preference persists when switching between images
        setToothPartsMasterEnabled(toothPartsDefaultOn());
      }
      setLoadedPhotoId(currentPhotoId);
    }
  });

  const hoveredAnnotationTooltip = createMemo(() => {
    const id = hoveredAnnotationId();
    if (!id) return null;
    const data = pearlData();
    if (!data) return null;
    const annotation = data.annotations.find((a) => a.enml_annotation_id === id);
    if (!annotation) return null;
    return formatAnnotationTooltip(annotation);
  });

  const categories = createMemo(() => {
    const data = pearlData();
    if (!data) return [];
    return getCategories(data.annotations, data.toothParts);
  });

  createEffect(() => {
    const cats = categories();
    const currentLoadedId = loadedPhotoId();
    if (cats.length > 0 && currentLoadedId && defaultsAppliedFor() !== currentLoadedId) {
      setDefaultsAppliedFor(currentLoadedId);
      setVisibleCategories(defaultVisibleCategories(cats, toothPartsDefaultOn()));
    }
  });

  const visibleAnnotations = createMemo(() => {
    if (!allAnnotationsVisible()) return [];
    const data = pearlData();
    if (!data || props.photoId !== loadedPhotoId()) return [];
    return shownAnnotations(data, visibleCategories());
  });

  const visibleToothParts = createMemo(() => {
    if (!allAnnotationsVisible()) return [];
    const data = pearlData();
    if (!data?.toothParts || props.photoId !== loadedPhotoId()) return [];
    return shownToothParts(data, visibleCategories());
  });

  const categoryCounts = createMemo(() => {
    const counts: Record<string, number> = {};
    const data = pearlData();
    if (!data) return counts;

    for (const annotation of data.annotations) {
      if (annotation.is_dismissed || annotation.is_deleted) continue;
      const category = getAnnotationCategory(annotation);
      counts[category] = (counts[category] ?? 0) + 1;
    }
    for (const toothPart of data.toothParts ?? []) {
      const category = normalizeCategory(toothPart.condition);
      counts[category] = (counts[category] ?? 0) + 1;
    }
    return counts;
  });

  const groupedCategories = createMemo(() => {
    const cats = categories();
    const groups: Record<CategoryGroup, string[]> = {
      pathology: [],
      non_pathology: [],
      tooth_parts: [],
    };
    for (const cat of cats) {
      const config = CATEGORY_CONFIG[cat];
      const group = config?.group || "non_pathology";
      groups[group].push(cat);
    }
    return groups;
  });

  const groupCount = (group: CategoryGroup) => {
    const counts = categoryCounts();
    return groupedCategories()[group].reduce((sum, cat) => sum + (counts[cat] ?? 0), 0);
  };

  const pearlDimensions = createMemo(() => {
    const data = pearlData();
    if (!data) return { width: 1, height: 1 };
    return { width: data.width, height: data.height };
  });

  const imagePxPerScreenPxAtFit = createMemo(() => {
    const bounds = props.imageBounds;
    if (!bounds) return 1;
    const dims = pearlDimensions();
    const fitLongSide = Math.max(bounds.width, bounds.height) / (props.zoomPercent / 100);
    return Math.max(dims.width, dims.height) / fitLongSide;
  });

  const toggleCategory = (category: string) => {
    setVisibleCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const [toothPartsMasterEnabled, setToothPartsMasterEnabled] = createSignal(toothPartsDefaultOn());

  const toggleAllToothParts = (enabled: boolean) => {
    setToothPartsMasterEnabled(enabled);
    setVisibleCategories((prev) => {
      const next = new Set(prev);
      const toothParts = groupedCategories().tooth_parts;
      for (const cat of toothParts) {
        if (enabled) {
          next.add(cat);
        } else {
          next.delete(cat);
        }
      }
      return next;
    });
  };

  return (
    <Show when={props.hasPearlData}>
      <div
        style={{
          display: props.visible === false || props.imageReady === false ? "none" : "block",
        }}
      >
        <Show
          when={
            fetchResult.loading ||
            isPolling() ||
            reanalyzing() ||
            pollExhausted() ||
            props.photoId !== loadedPhotoId()
          }
        >
          <div class="absolute top-3 right-3 z-10 flex max-h-[calc(50%-2rem)] flex-col gap-2 overflow-hidden">
            <div class="flex items-center gap-2 rounded bg-gray-800/90 px-3 py-1.5 text-xs text-white">
              <Show
                when={!pollExhausted()}
                fallback={
                  <>
                    <span>Analysis timed out</span>
                    <button
                      type="button"
                      onClick={handleReanalyze}
                      class="ml-1 rounded bg-white/20 px-2 py-0.5 text-[10px] font-medium transition hover:bg-white/30"
                    >
                      Retry
                    </button>
                  </>
                }
              >
                <span class="size-1.5 animate-pulse rounded-full bg-blue-400" />
                <span>Analyzing...</span>
              </Show>
            </div>
            <Show when={props.belowPanelContent}>{props.belowPanelContent!()}</Show>
          </div>
        </Show>

        <Show
          when={
            pearlData() &&
            props.photoId === loadedPhotoId() &&
            (pearlData()!.annotations.length > 0 || (pearlData()!.toothParts?.length ?? 0) > 0) &&
            props.imageBounds &&
            !props.hideAnnotations
          }
        >
          <PearlShapes
            class="pointer-events-none absolute"
            style={{
              left: `${props.imageBounds!.left}px`,
              top: `${props.imageBounds!.top}px`,
              width: `${props.imageBounds!.width}px`,
              height: `${props.imageBounds!.height}px`,
            }}
            data={pearlData()!}
            annotations={visibleAnnotations()}
            toothParts={visibleToothParts()}
            imagePxPerScreenPx={imagePxPerScreenPxAtFit()}
            hoveredAnnotationId={hoveredAnnotationId()}
            onAnnotationHover={(id, point) => {
              setHoveredAnnotationId(id);
              setTooltipPosition(point);
            }}
          />

          {/* Panel container - always visible for adjustments, Pearl controls only when loaded */}
          <div class="absolute top-3 right-3 z-10 flex max-h-[calc(50%-2rem)] flex-col gap-2 overflow-auto">
            <Show when={props.photoId === loadedPhotoId()}>
              <div class="flex min-h-0 w-52 flex-col overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-2xl">
                <div class="flex items-center justify-between border-b border-gray-700 bg-gray-800 px-3 py-2">
                  <label class="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allAnnotationsVisible()}
                      onChange={(e) => setAllAnnotationsVisible(e.currentTarget.checked)}
                      class="size-3 rounded accent-brand-primary"
                    />
                    <span class="text-xs font-medium text-white">Pearl AI</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleReanalyze}
                      disabled={reanalyzing()}
                      class="text-[10px] text-gray-400 transition hover:text-white disabled:opacity-50"
                      title="Re-run Pearl analysis on this image"
                    >
                      Redo
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsPanelExpanded(!isPanelExpanded())}
                      class="text-[10px] text-gray-400 transition hover:text-white"
                    >
                      {isPanelExpanded() ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <Show when={isPanelExpanded()}>
                  <div class="max-h-80 min-h-0 flex-1 overflow-y-auto p-2">
                    <For
                      each={(["pathology", "non_pathology", "tooth_parts"] as const).filter(
                        (g) => groupedCategories()[g].length > 0,
                      )}
                    >
                      {(groupKey) => (
                        <div class="mb-2 last:mb-0">
                          <div class="mb-1 flex items-center justify-between px-1 text-[10px] font-semibold tracking-wider text-gray-500 uppercase">
                            <span>{GROUP_LABELS[groupKey]}</span>
                            <Show when={groupKey !== "tooth_parts"}>
                              <span class="text-gray-400">{groupCount(groupKey)}</span>
                            </Show>
                          </div>

                          <Show
                            when={groupKey === "tooth_parts"}
                            fallback={
                              <div class="space-y-0.5">
                                <For each={groupedCategories()[groupKey]}>
                                  {(category) => {
                                    const config = CATEGORY_CONFIG[category] || {
                                      label: category,
                                      color: "#888",
                                      group: "non_pathology" as CategoryGroup,
                                    };
                                    return (
                                      <label class="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-gray-300 transition hover:bg-gray-800 hover:text-white">
                                        <input
                                          type="checkbox"
                                          checked={visibleCategories().has(category)}
                                          onChange={() => toggleCategory(category)}
                                          class="size-3 rounded"
                                          style={{ "accent-color": config.color }}
                                        />
                                        <span
                                          class="size-2 rounded-sm"
                                          style={
                                            category === "measurements"
                                              ? { background: MEASUREMENT_SWATCH }
                                              : { "background-color": config.color }
                                          }
                                        />
                                        <span class="flex-1">{config.label}</span>
                                        <span class="text-[10px] text-gray-500">
                                          {categoryCounts()[category] ?? 0}
                                        </span>
                                      </label>
                                    );
                                  }}
                                </For>
                              </div>
                            }
                          >
                            <label class="mb-1.5 flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-gray-300 transition hover:bg-gray-800 hover:text-white">
                              <input
                                type="checkbox"
                                checked={toothPartsMasterEnabled()}
                                onChange={(e) => toggleAllToothParts(e.currentTarget.checked)}
                                class="size-3 rounded"
                                style={{ "accent-color": "#C4C4C4" }}
                              />
                              <span class="flex-1 font-medium">Tooth parts</span>
                            </label>
                            <div class="grid grid-cols-2 gap-1 px-1">
                              <For each={groupedCategories()[groupKey]}>
                                {(category) => {
                                  const config = CATEGORY_CONFIG[category] || {
                                    label: category,
                                    color: "#888",
                                    group: "tooth_parts" as CategoryGroup,
                                  };
                                  const isChecked = () => visibleCategories().has(category);
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => toggleCategory(category)}
                                      class={`flex items-center gap-1.5 rounded px-1.5 py-1 text-[10px] transition ${
                                        isChecked()
                                          ? "bg-gray-700 text-white"
                                          : "text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                                      }`}
                                    >
                                      <span
                                        class="size-3 rounded-sm border border-gray-600"
                                        style={{
                                          "background-color": isChecked()
                                            ? config.color
                                            : "transparent",
                                        }}
                                      />
                                      <span>{config.label}</span>
                                    </button>
                                  );
                                }}
                              </For>
                            </div>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            </Show>
            {/* Render additional content below the panel (e.g., adjustments) - always visible */}
            <Show when={props.belowPanelContent}>{props.belowPanelContent!()}</Show>
          </div>
        </Show>

        <Show when={hoveredAnnotationTooltip() && tooltipPosition()}>
          {(() => {
            const tooltip = hoveredAnnotationTooltip()!;
            const pos = tooltipPosition()!;
            return (
              <div
                class="pointer-events-none fixed z-50 rounded-lg border border-gray-600 bg-gray-900/95 px-3 py-2 shadow-xl backdrop-blur-sm"
                style={{
                  left: `${pos.x + 12}px`,
                  top: `${pos.y + 12}px`,
                }}
              >
                <div class="text-xs font-medium text-white">{tooltip.title}</div>
                <Show when={tooltip.subtitle}>
                  <div class="mt-1 text-[11px] text-gray-300">{tooltip.subtitle}</div>
                </Show>
              </div>
            );
          })()}
        </Show>
      </div>
    </Show>
  );
}
